/**
 * Essential Oil Selector — multi-slot combobox picker.
 *
 * Renders N "Scent" slots based on the current variant. Each slot is a
 * custom combobox showing [thumbnail] NAME / DESCRIPTION and a listbox
 * where every option shows [thumbnail] NAME + DESCRIPTION.
 *
 * Cart format: one hidden <input name="properties[Essential Oil – Option N]">
 * per slot. cart-drawer liquid aggregates these by "Essential Oil / Option"
 * prefix for display.
 */
(function () {
  'use strict';

  class EssentialOilSelector extends HTMLElement {
    constructor() {
      super();
      this.oils = [];
      this.variantSlots = {};
      this.defaultsByCountry = {};
      this.country = this.dataset.country || 'default';
      this.slotsEl = this.querySelector('[data-slots]');
      this.inputsEl = this.querySelector('[data-oil-inputs]');
      this.currentSelections = []; // oil id per slot; '' means unselected
      this.currentVariantId = null;
      this.activeSlotIdx = -1;
      this._onDocClick = this._onDocClick.bind(this);
    }

    connectedCallback() {
      this.oils = this._parseOils();
      if (!this.oils.length) return;
      this._appendRandomOil();
      try { this.variantSlots = JSON.parse(this.dataset.variantSlots || '{}'); } catch (e) {}
      this.defaultsByCountry = this._parseDefaults();

      this._renderForCurrentVariant();

      document.addEventListener('variant:change', (e) => this._onVariantChange(e));
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, (payload) => {
          if (payload && payload.data && payload.data.variant) this._setVariant(payload.data.variant.id);
        });
      }
      this._observeVariantInput();
      document.addEventListener('click', this._onDocClick);
      this._attachSubmitGuard();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
    }

    _randomEnabled() {
      return ['14930200723782', '15530691297606'].indexOf(String(this.dataset.productId)) !== -1;
    }

    _appendRandomOil() {
      if (!this._randomEnabled() || !this.oils.length || this.oils.some(o => o.id === 'warehouse-random')) return;
      // Append to the data pool so existing default ordering never changes.
      this.oils.push({ id: 'warehouse-random', code: 'warehouse-random',
        name: this.dataset.randomLabel || 'Random scent',
        desc: this.dataset.randomDescription || 'Chosen at dispatch. The scent may vary with each delivery.', image: '' });
    }

    _parseOils() {
      if (this.dataset.oils) {
        try { return JSON.parse(this.dataset.oils); } catch (e) {}
      }
      var fb = document.getElementById('EssentialOilsFallback');
      if (fb) { try { return JSON.parse(fb.textContent); } catch (e) {} }
      return [];
    }

    _parseDefaults() {
      if (this.dataset.defaultsByCountry) {
        try { return JSON.parse(this.dataset.defaultsByCountry); } catch (e) {}
      }
      var fb = document.getElementById('EssentialOilDefaultsFallback');
      if (fb) { try { return JSON.parse(fb.textContent); } catch (e) {} }
      return {};
    }

    _getSlotCountForVariant(variantId) {
      var entry = this.variantSlots[String(variantId)];
      if (entry && typeof entry.slots === 'number') return entry.slots;

      // Set count (8/10/12-piece collections) is locale-invariant: parse the
      // product HANDLE first ("...30ml-x-8", "...-8-set..."), which never gets
      // translated. Falling back to the localized title here would break in
      // non-English storefronts — e.g. JA titles drop the word "piece" and the
      // leftover "30ml" matches the size branch, collapsing 8 slots down to 1.
      var setCands = [];
      if (this.dataset.productHandle) setCands.push(this.dataset.productHandle);
      if (entry && entry.title) setCands.push(entry.title);
      if (this.dataset.productTitle) setCands.push(this.dataset.productTitle);
      for (var i = 0; i < setCands.length; i++) {
        var s = this._parseSetCount(setCands[i]);
        if (s) return s;
      }

      // Single-bottle / ritual products: slots come from the chosen ml size.
      // Use the (localized) variant/product title — NOT the handle, whose
      // "30ml-x-8" ml token would mislead. Sets are already handled above.
      var sizeCands = [];
      if (entry && entry.title) sizeCands.push(entry.title);
      if (this.dataset.productTitle) sizeCands.push(this.dataset.productTitle);
      for (var j = 0; j < sizeCands.length; j++) {
        var z = this._parseSizeCount(sizeCands[j]);
        if (z) return z;
      }
      return 0;
    }

    // Number of bottles in a set, from language-invariant tokens.
    _parseSetCount(text) {
      var t = (text || '').toLowerCase();
      // "30ml-x-8", "30ml × 12", "x8"
      var mX = t.match(/[x×]\s*-?\s*(\d+)\b/);
      if (mX) return parseInt(mX[1], 10);
      // "8-piece", "10 piece"
      var mPiece = t.match(/(\d+)[\s-]*piece/);
      if (mPiece) return parseInt(mPiece[1], 10);
      // "8-set", "12set" (but not "set" alone)
      var mSet = t.match(/(\d+)[\s-]*set\b/);
      if (mSet) return parseInt(mSet[1], 10);
      // CJK counters that survive translation: 8本 / 8瓶 / 8個 / 8点 / 8種.
      // Real sets are 8/10/12 bottles (5+), so require >= 5: JA promo text like
      // "120ml（3本購入で1本無料）" contains "3本"/"1本" and must fall through to
      // the ml-size branch (120ml -> 4) instead of being read as a 3-bottle set.
      var mCjk = t.match(/(\d+)\s*(?:本|瓶|個|点|種)/);
      if (mCjk) {
        var cjkCount = parseInt(mCjk[1], 10);
        if (cjkCount >= 5) return cjkCount;
      }
      return 0;
    }

    // Slots from a single ml size: 30/60/90/120ml -> 1/2/3/4.
    _parseSizeCount(text) {
      var t = (text || '').toLowerCase();
      var mSize = t.match(/(\d+)\s*ml/);
      if (mSize) {
        var ml = parseInt(mSize[1], 10);
        if (ml <= 30) return 1;
        if (ml <= 60) return 2;
        if (ml <= 90) return 3;
        if (ml <= 120) return 4;
      }
      // Arabic: 'مل' = ml, but number comes after مل in string ("مل / 1 fl oz 30")
      if (t.indexOf('مل') !== -1) {
        var nums = (t.match(/\d+/g) || []).map(Number);
        var known = [30, 60, 90, 120];
        for (var k = 0; k < known.length; k++) {
          if (nums.indexOf(known[k]) !== -1) {
            var ml2 = known[k];
            if (ml2 <= 30) return 1;
            if (ml2 <= 60) return 2;
            if (ml2 <= 90) return 3;
            if (ml2 <= 120) return 4;
          }
        }
      }
      return 0;
    }

    _getDefaults(n) {
      var result = [];
      // 套装（5+ slot）：先按槽位数取 set_by_count["8"|"10"|"12"]（允许各尺寸独立排列，
      // 例如 12-set 保留不可避免的重复瓶），未命中回退通用 "set"，再未命中按精油池编号顺序补
      if (n >= 5) {
        var byCount = (this.defaultsByCountry && this.defaultsByCountry['set_by_count']) || {};
        var setList = byCount[String(n)] || (this.defaultsByCountry && this.defaultsByCountry['set']) || [];
        for (var i = 0; i < n; i++) {
          if (setList[i] && this._oilById(setList[i])) {
            result.push(setList[i]);
          } else {
            result.push(this.oils[i] ? this.oils[i].id : '');
          }
        }
        return result;
      }
      // 1-4 slot（单品 / 小套）按国家偏好；不够再按编号补
      var list = this._resolveCountryList();
      for (var j = 0; j < n; j++) {
        if (list[j] && this._oilById(list[j])) {
          result.push(list[j]);
        } else {
          result.push(this.oils[j] ? this.oils[j].id : '');
        }
      }
      return result;
    }

    _resolveCountryList() {
      // Lookup priority: direct country code → _groups membership → "default"
      var c = this.defaultsByCountry || {};
      var country = this.country;
      if (c[country] && c[country].length) return c[country];
      var groups = c._groups || {};
      for (var g in groups) {
        if (groups.hasOwnProperty(g) && Object.prototype.toString.call(groups[g]) === '[object Array]'
            && groups[g].indexOf(country) !== -1
            && c[g] && c[g].length) {
          return c[g];
        }
      }
      return c['default'] || [];
    }

    _oilById(id) {
      if (!id) return null;
      var idStr = String(id);
      // Exact id match (oil.id is the locale-invariant product GID)
      for (var i = 0; i < this.oils.length; i++) {
        if (String(this.oils[i].id) === idStr) return this.oils[i];
      }
      // Code match: country defaults use short slugs ("no-29"); oil.code is the locale-invariant slug
      var idLower = idStr.toLowerCase();
      for (var j = 0; j < this.oils.length; j++) {
        if (String(this.oils[j].code || '').toLowerCase() === idLower) return this.oils[j];
      }
      return null;
    }

    _getCurrentVariantId() {
      var form = document.getElementById(this.dataset.formId);
      if (!form) return null;
      var input = form.querySelector('[name="id"]');
      return input ? input.value : null;
    }

    _renderForCurrentVariant() {
      this._setVariant(this._getCurrentVariantId());
    }

    _setVariant(variantId) {
      if (!variantId) { this._render(0); return; }
      this.currentVariantId = variantId;
      var count = this._getSlotCountForVariant(variantId);
      this._render(count);
    }

    _onVariantChange(e) {
      var v = (e && e.detail && e.detail.variant) || (e && e.detail);
      if (v && v.id) this._setVariant(v.id);
    }

    _observeVariantInput() {
      var form = document.getElementById(this.dataset.formId);
      if (!form) return;
      var input = form.querySelector('[name="id"]');
      if (!input) return;
      var last = input.value;
      var mo = new MutationObserver(() => {
        if (input.value !== last) {
          last = input.value;
          this._setVariant(last);
        }
      });
      mo.observe(input, { attributes: true, attributeFilter: ['value'] });
    }

    _render(count) {
      if (!this.slotsEl) return;
      if (!count || count < 1) {
        this.slotsEl.innerHTML = '';
        this.currentSelections = [];
        this._writeInputs();
        this.hidden = true;
        return;
      }
      this.hidden = false;

      var prev = this.currentSelections.slice();
      var defaults = this._getDefaults(count);
      var next = [];
      for (var i = 0; i < count; i++) {
        // Preserve previous selection if still valid
        if (prev[i] && this._oilById(prev[i])) {
          next.push(prev[i]);
        } else {
          next.push(defaults[i] || '');
        }
      }
      this.currentSelections = next;

      var html = '';
      for (var j = 0; j < count; j++) {
        html += this._slotHtml(j, next[j]);
      }
      this.slotsEl.innerHTML = html;

      // Bind events
      var slots = this.slotsEl.querySelectorAll('.eos__slot');
      slots.forEach((slot, idx) => this._bindSlot(slot, idx));

      this._writeInputs();
    }

    _slotHtml(idx, selectedId) {
      var labelTpl = this.dataset.slotLabel || 'Scent %count%';
      var label = labelTpl.replace('%count%', String(idx + 1));
      var placeholder = this.dataset.placeholder || 'Choose a scent';
      var requiredLabel = this.dataset.requiredLabel || 'required';
      var errorMsg = this.dataset.errorRequired || 'Please choose an option.';
      var oil = this._oilById(selectedId);
      var comboThumb = this._thumbHtml(oil, 'eos__combo-thumb', false);
      var name = oil ? this._esc(oil.name) : this._esc(placeholder);
      var desc = oil ? this._esc(oil.desc || '') : '';
      var isEmpty = !oil;
      var listboxId = 'eos-listbox-' + this._uid() + '-' + idx;
      var comboId = 'eos-combo-' + this._uid() + '-' + idx;

      // listbox options are NOT rendered here — they're built lazily on first open
      // to avoid creating N*12 DOM nodes + triggering preload of N*12 images.
      return (
        '<div class="eos__slot" data-slot-index="' + idx + '">' +
          '<label class="eos__label" for="' + comboId + '">' + this._esc(label) +
            '<span class="eos__required" aria-label="' + this._esc(requiredLabel) + '">*</span></label>' +
          '<button type="button" class="eos__combo' + (isEmpty ? ' is-empty' : '') + '" ' +
            'id="' + comboId + '" ' +
            'role="combobox" aria-haspopup="listbox" aria-expanded="false" ' +
            'aria-controls="' + listboxId + '" data-combo>' +
            comboThumb +
            '<div class="eos__combo-text">' +
              '<span class="eos__combo-name">' + name + '</span>' +
              (desc ? '<span class="eos__combo-desc">' + desc + '</span>' : '') +
            '</div>' +
            '<svg class="eos__caret" viewBox="0 0 12 8" fill="none" aria-hidden="true">' +
              '<path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
          '</button>' +
          '<ul class="eos__listbox" role="listbox" id="' + listboxId + '" ' +
            'aria-label="' + this._esc(label) + '" tabindex="-1" data-listbox-empty="1"></ul>' +
          '<div class="eos__error" data-error>' + this._esc(errorMsg) + '</div>' +
        '</div>'
      );
    }

    _buildListboxOptions(slot, idx) {
      var listbox = slot.querySelector('[role="listbox"]');
      if (!listbox || !listbox.hasAttribute('data-listbox-empty')) return;
      var selectedId = this.currentSelections[idx];
      var listboxId = listbox.id;
      var displayOils = this.oils.filter(o => o.id === 'warehouse-random').concat(this.oils.filter(o => o.id !== 'warehouse-random'));
      var html = displayOils.map((o, i) => {
        var sel = o.id === selectedId;
        return (
          '<li class="eos__option" role="option" id="' + listboxId + '-opt-' + i + '" ' +
            'data-oil-id="' + this._esc(o.id) + '" aria-selected="' + (sel ? 'true' : 'false') + '">' +
            this._thumbHtml(o, 'eos__option-thumb', true) +
            '<div class="eos__option-text">' +
              '<span class="eos__option-name">' + this._esc(o.name) + '</span>' +
              (o.desc ? '<span class="eos__option-desc">' + this._esc(o.desc) + '</span>' : '') +
            '</div>' +
          '</li>'
        );
      }).join('');
      listbox.innerHTML = html;
      listbox.removeAttribute('data-listbox-empty');
      // Bind option events now that they exist
      listbox.querySelectorAll('.eos__option').forEach((opt) => {
        opt.addEventListener('click', (e) => {
          e.stopPropagation();
          this._select(idx, opt.dataset.oilId);
          this._closeListbox(idx);
          var combo = slot.querySelector('[data-combo]');
          if (combo) combo.focus();
        });
        opt.addEventListener('mouseenter', () => this._highlight(idx, opt));
      });
    }

    _thumbHtml(oil, cls, lowPriority) {
      if (oil && oil.image) {
        var attrs = 'loading="lazy" decoding="async"';
        if (lowPriority) attrs += ' fetchpriority="low"';
        return '<div class="' + cls + '"><img src="' + this._esc(oil.image) + '" alt="' + this._esc(oil.name) + '" ' + attrs + '></div>';
      }
      return '<div class="' + cls + '"><span class="' + cls + '--empty" aria-hidden="true">\u2726</span></div>';
    }

    _bindSlot(slot, idx) {
      var combo = slot.querySelector('[data-combo]');
      var listbox = slot.querySelector('[role="listbox"]');
      if (!combo || !listbox) return;

      combo.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleListbox(idx);
      });
      combo.addEventListener('keydown', (e) => this._onComboKeydown(e, idx));
      // Option events are bound later in _buildListboxOptions (lazy)
      listbox.addEventListener('keydown', (e) => this._onListboxKeydown(e, idx));
    }

    _toggleListbox(idx) {
      var slot = this.slotsEl.querySelector('[data-slot-index="' + idx + '"]');
      var listbox = slot.querySelector('[role="listbox"]');
      var combo = slot.querySelector('[data-combo]');
      var isOpen = listbox.classList.contains('is-open');
      // Lazy-build options on first open to keep initial DOM light.
      if (!isOpen) this._buildListboxOptions(slot, idx);
      // Close any other open listboxes
      this.slotsEl.querySelectorAll('[role="listbox"].is-open').forEach((lb) => {
        if (lb !== listbox) {
          lb.classList.remove('is-open');
          var otherSlot = lb.closest('.eos__slot');
          if (otherSlot) otherSlot.classList.remove('is-open');
        }
      });
      this.slotsEl.querySelectorAll('[data-combo][aria-expanded="true"]').forEach((c) => {
        if (c !== combo) c.setAttribute('aria-expanded', 'false');
      });
      if (isOpen) {
        this._closeListbox(idx);
      } else {
        slot.classList.add('is-open');
        listbox.classList.add('is-open');
        combo.setAttribute('aria-expanded', 'true');
        this.activeSlotIdx = idx;
        // Highlight currently selected option
        var selected = listbox.querySelector('[aria-selected="true"]');
        if (selected) {
          this._highlight(idx, selected);
          selected.scrollIntoView({ block: 'nearest' });
        } else {
          var first = listbox.querySelector('.eos__option');
          if (first) this._highlight(idx, first);
        }
      }
    }

    _closeListbox(idx) {
      var slot = this.slotsEl.querySelector('[data-slot-index="' + idx + '"]');
      if (!slot) return;
      var listbox = slot.querySelector('[role="listbox"]');
      var combo = slot.querySelector('[data-combo]');
      slot.classList.remove('is-open');
      listbox.classList.remove('is-open');
      combo.setAttribute('aria-expanded', 'false');
      // Clear highlight
      listbox.querySelectorAll('.is-highlighted').forEach((el) => el.classList.remove('is-highlighted'));
    }

    _highlight(idx, optionEl) {
      var slot = this.slotsEl.querySelector('[data-slot-index="' + idx + '"]');
      var listbox = slot.querySelector('[role="listbox"]');
      listbox.querySelectorAll('.is-highlighted').forEach((el) => el.classList.remove('is-highlighted'));
      optionEl.classList.add('is-highlighted');
    }

    _select(idx, oilId) {
      this.currentSelections[idx] = oilId;
      // Update combo UI
      var slot = this.slotsEl.querySelector('[data-slot-index="' + idx + '"]');
      if (!slot) return;
      var combo = slot.querySelector('[data-combo]');
      var oil = this._oilById(oilId);
      var thumbEl = combo.querySelector('.eos__combo-thumb');
      var nameEl = combo.querySelector('.eos__combo-name');
      var descEl = combo.querySelector('.eos__combo-desc');

      if (thumbEl) thumbEl.outerHTML = this._thumbHtml(oil, 'eos__combo-thumb', false);
      if (nameEl) nameEl.textContent = oil ? oil.name : (this.dataset.placeholder || 'Choose a scent');
      if (oil && oil.desc) {
        if (descEl) descEl.textContent = oil.desc;
        else {
          var textWrap = combo.querySelector('.eos__combo-text');
          if (textWrap) {
            var d = document.createElement('span');
            d.className = 'eos__combo-desc';
            d.textContent = oil.desc;
            textWrap.appendChild(d);
          }
        }
      } else if (descEl) {
        descEl.remove();
      }
      combo.classList.toggle('is-empty', !oil);
      combo.classList.remove('is-invalid');
      var err = slot.querySelector('[data-error]');
      if (err) err.classList.remove('is-visible');

      // Update aria-selected on options
      var listbox = slot.querySelector('[role="listbox"]');
      listbox.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', opt.dataset.oilId === oilId ? 'true' : 'false');
      });

      this._writeInputs();
    }

    _onComboKeydown(e, idx) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        var slot = this.slotsEl.querySelector('[data-slot-index="' + idx + '"]');
        var listbox = slot.querySelector('[role="listbox"]');
        if (!listbox.classList.contains('is-open')) {
          this._toggleListbox(idx);
        }
        // Move focus into listbox
        var highlighted = listbox.querySelector('.is-highlighted') || listbox.querySelector('.eos__option');
        if (highlighted) listbox.focus();
      } else if (e.key === 'Escape') {
        this._closeListbox(idx);
      }
    }

    _onListboxKeydown(e, idx) {
      var slot = this.slotsEl.querySelector('[data-slot-index="' + idx + '"]');
      var listbox = slot.querySelector('[role="listbox"]');
      var combo = slot.querySelector('[data-combo]');
      var options = Array.from(listbox.querySelectorAll('.eos__option'));
      var currentIdx = options.findIndex((o) => o.classList.contains('is-highlighted'));

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        var next = options[Math.min(currentIdx + 1, options.length - 1)];
        if (next) { this._highlight(idx, next); next.scrollIntoView({ block: 'nearest' }); }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        var prev = options[Math.max(currentIdx - 1, 0)];
        if (prev) { this._highlight(idx, prev); prev.scrollIntoView({ block: 'nearest' }); }
      } else if (e.key === 'Home') {
        e.preventDefault();
        if (options[0]) { this._highlight(idx, options[0]); options[0].scrollIntoView({ block: 'nearest' }); }
      } else if (e.key === 'End') {
        e.preventDefault();
        var last = options[options.length - 1];
        if (last) { this._highlight(idx, last); last.scrollIntoView({ block: 'nearest' }); }
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        var hl = options[currentIdx];
        if (hl) {
          this._select(idx, hl.dataset.oilId);
          this._closeListbox(idx);
          combo.focus();
        }
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        this._closeListbox(idx);
        if (e.key === 'Escape') combo.focus();
      }
    }

    _onDocClick(e) {
      if (!this.contains(e.target)) {
        this.slotsEl.querySelectorAll('[role="listbox"].is-open').forEach((lb) => {
          var slotEl = lb.closest('.eos__slot');
          if (slotEl) this._closeListbox(parseInt(slotEl.dataset.slotIndex, 10));
        });
      }
    }

    _writeInputs() {
      if (!this.inputsEl) return;
      this.inputsEl.innerHTML = '';
      var formId = this.dataset.formId;
      var randomSlots = [];
      this.currentSelections.forEach((id, i) => {
        var oil = this._oilById(id);
        if (!oil) return; // empty slots skip — still validated on submit
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Essential Oil \u2013 Option ' + (i + 1) + ']';
        var isRandom = this._randomEnabled() && id === 'warehouse-random';
        input.value = isRandom ? 'Random scent' : oil.name;
        if (isRandom) randomSlots.push(i + 1);
        if (formId) input.setAttribute('form', formId);
        this.inputsEl.appendChild(input);
      });
      if (randomSlots.length) {
        var marker = document.createElement('input');
        marker.type = 'hidden';
        marker.name = 'properties[_charabanc_random_scent_slots]';
        marker.value = JSON.stringify(randomSlots);
        if (formId) marker.setAttribute('form', formId);
        this.inputsEl.appendChild(marker);
      }
    }

    _attachSubmitGuard() {
      var form = document.getElementById(this.dataset.formId);
      if (!form) return;
      form.addEventListener('submit', (e) => {
        if (this.hidden) return;
        var firstInvalid = null;
        this.currentSelections.forEach((id, idx) => {
          if (!id) {
            var slot = this.slotsEl.querySelector('[data-slot-index="' + idx + '"]');
            var combo = slot && slot.querySelector('[data-combo]');
            var err = slot && slot.querySelector('[data-error]');
            if (combo) combo.classList.add('is-invalid');
            if (err) err.classList.add('is-visible');
            if (!firstInvalid) firstInvalid = combo;
          }
        });
        if (firstInvalid) {
          e.preventDefault();
          e.stopImmediatePropagation();
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalid.focus();
        }
      }, true);
    }

    _uid() {
      if (!this._uidCache) this._uidCache = Math.random().toString(36).slice(2, 8);
      return this._uidCache;
    }

    _esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get('essential-oil-selector')) {
    customElements.define('essential-oil-selector', EssentialOilSelector);
  }
})();
